import { IEmptyObject } from "@Components/shared/types";
import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import SessionManager from "@Core/session";
import { IServiceUser } from "@Core/session/IServiceUser";
import { IEntity } from "@Models/IEntity";
import { ILegacyDetails } from "@Models/ILegacyDetails";
import { ILoginDetails, IResetPasswordModel } from "@Models/ILoginDetails";
import { IRegisterDetails, ISetPasswordDetails } from "@Models/IRegisterDetails";
import { IUserInitial } from "@Models/IUserDetails";
import { IValue } from "@Models/IValue";

export default class AccountService extends ServiceBase {
    /**
     * Verify account registration payload.
     * @param payload The registration payload to verify
     */
    public async verifyAccount(payload: string): Promise<Result<string>> {
        return await this.requestJson({
            url: `/api/Account/VerifyAccount`,
            method: "PATCH",
            data: JSON.stringify(payload),
            headers: {
                Accept: "*/*",
                "Content-Type": "application/json",
            },
        });
    }

    /**
     * Complete user registration with form details.
     * @param formPayload Registration details from form
     */
    public async completeRegistration(formPayload: IRegisterDetails): Promise<Result<IUserInitial>> {
        return await this.requestJson<IUserInitial>({
            url: `/api/account/CompleteRegistration`,
            method: "PATCH",
            data: formPayload,
            headers: {
                Accept: "*/*",
                "Content-Type": "application/json",
            },
        });
    }

    public async login(loginModel: ILoginDetails): Promise<Result<IServiceUser>> {
        const result = await this.requestJson<IServiceUser>({
            url: `/api/Account/Login`,
            method: "POST",
            data: loginModel,
        });

        if (!result.hasErrors) {
            SessionManager.setServiceUser(result.value);
        }

        return result;
    }

    public async logout(): Promise<Result<IEmptyObject>> {
        const result = await this.requestJson<IEmptyObject>({
            url: `/api/Account/Logout`,
            method: "POST",
        });

        if (!result.hasErrors) {
            SessionManager.deleteServiceUser();
        }

        return result;
    }

    public async create(user: IUserInitial, groupId: string, roleIds: string[]): Promise<Result<IEntity[]>> {
        return await this.requestJson({
            url: `/api/Account/Create/${groupId}/${roleIds.join(",")}`,
            method: "POST",
            data: [user],
        });
    }

    public async upload(groupId: string, users: FormData): Promise<Result<IEntity[]>> {
        return await this.requestJson({
            url: `/api/Account/Upload/${groupId}`,
            method: "POST",
            data: users,
        });
    }

    public async reset(email: IResetPasswordModel): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Account/ResetPassword`,
            method: "POST",
            data: email,
        });
    }

    public async resendRegistration(userId: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Account/ResendRegistration/${userId}`,
            method: "POST",
        });
    }

    public async setPassword(model: ISetPasswordDetails): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Account/SetPassword`,
            method: "POST",
            data: model,
        });
    }

    public async updatePassword(userId: string, model: IValue): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Account/UpdatePassword/${userId}`,
            method: "POST",
            data: model,
        });
    }

    public async activateLegacy(model: ILegacyDetails): Promise<Result<IServiceUser>> {
        const result = await this.requestJson<IServiceUser>({
            url: `/api/Account/ActivateLegacy`,
            method: "POST",
            data: model,
        });

        if (!result.hasErrors) {
            SessionManager.setServiceUser(result.value);
        }

        return result;
    }

    public async deactivate(): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `/api/Account/Deactivate`,
            method: "PATCH",
        });
    }

    public async delete(userIds: string[]): Promise<Result<IEntity[]>> {
        return await this.requestJson({
            url: `/api/Account/Delete/${userIds.join(",")}`,
            method: "DELETE",
        });
    }

    public async toggleSuspension(userIds: string[], enabled: boolean): Promise<Result<IEntity[]>> {
        return await this.requestJson({
            url: `/api/Account/ToggleSuspension/${userIds.join(",")}?enabled=${enabled}`,
            method: "PATCH",
        });
    }
}
